/** 个人中心-权益管理 */
import { omit } from 'lodash-es';
import { fetch } from '../request';
import { USER_LEVEL_KEY, type IResponse } from '../interface';
import type { IDataBasic, RequestConfig, IAirWallexPaymentBasic } from '../interface/common';
import type { IOrder, ISubscription, IPostCreateOrderConfig } from '../interface/order';
import {
  type IEquity,
  PAY_TYPE,
  // PAY_STATUS,
  type TRightDisplay,
  ORDER_TYPE,
  ORDER_BEHAVIOR,
  PAY_STATUS,
  // BUY_TYPE,
} from '../interface/rights';
import { DISCOUNT_CODE } from '~/constants/config';

export function reqRights(faq?: boolean) {
  return fetch<IResponse<Array<IEquity>>>('/rights/info/', {
    method: 'GET',
    params: { many: true, faq },
  });
}

export function reqRightsDisplay(params?: any) {
  return fetch<IResponse<TRightDisplay>>('/rights/display/', {
    method: 'GET',
    params: { many: true, ...params },
  });
}

export interface IPostOrderClientBody {
  behavior: ORDER_BEHAVIOR;
  pay_type: PAY_TYPE;
  rights_type: USER_LEVEL_KEY;
  time_identity: string;
}

export interface IOrderResult {
  results: IOrder[];
  count: number;
  could_apply_invoice_total_count: number;
}

export function reqCreateOrder(body: IPostOrderClientBody, config?: RequestConfig) {
  const req = useRequestURL();
  const isMock = req.searchParams.has('mock');

  if (isMock) {
    body = Object.assign(body, {
      mock: {
        order_status: 'success',
      },
    });
  }

  return fetch<IResponse<IOrder>>('/order/client/', {
    method: 'POST',
    body: {
      ...body,
    },
    ...config,
  });
}

export interface IPostOrderClientBuyCreditCoinsBody {
  pay_type: PAY_TYPE;
  credit_coins_package_id: number;
  num?: number;
  first_name?: string;
  last_name?: string;
  email?: string;
}

export function postOrderClientBuyCreditPackage(
  body: IPostOrderClientBuyCreditCoinsBody,
  config?: RequestConfig
) {
  return fetch<IResponse<IOrder>>('/order/client/buy_credit_package/', {
    method: 'POST',
    body,
    ...config,
  });
}

export interface IPostOrderClientBuyStorageBody extends IStripePaymentBasic {
  pay_type: PAY_TYPE;
  num: number;
  is_GB: boolean;
}

export function postOrderClientBuyStorage(
  body: IPostOrderClientBuyStorageBody,
  config?: RequestConfig
) {
  return fetch<IResponse<IOrder>>('/order/client/buy_storage/', {
    method: 'POST',
    body,
    ...config,
  });
}

export function reqChangePayType(body: { id: number; pay_type: PAY_TYPE }, config?: RequestConfig) {
  return fetch<IResponse<IOrder>>('/order/client/pay_type/', {
    method: 'POST',
    body,
    ...config,
  });
}
export function reqOrder(id: number, config?: RequestConfig) {
  return fetch<IResponse<IOrder>>('/order/client/detail/', {
    method: 'GET',
    params: { id },
    ...config,
  });
}
export interface ICreditCoinOrder extends Omit<IOrder, 'status'> {
  total_credit_coin: number; // 总言币
  remained_credit_coin: number; // 剩余言币
  start_time: string; // 生效时间
  start_timestamp: number; // 生效时间
  expired_time: string; // 失效时间
  expired_timestamp: number; // 失效时间
  order_type: ORDER_TYPE;
  status: 1 | 99; // 1:未失效 99:失效
  is_show: boolean;
  rights_name: string;
  rights_type: USER_LEVEL_KEY;
  time_identity: string;
}
// 权益管理订单
export function reqCreditCoinOrder(params: any) {
  return fetch<IResponse<{ order_list: ICreditCoinOrder[] }>>('/order/client/my_orders/', {
    method: 'GET',
    params,
  });
}

// 获取可用的言币加油包
interface IGetRightsCreditCoinsPackageParams {
  many: string;
  rights_type: USER_LEVEL_KEY;
  use_cache?: number;
}
export interface ICreditCoinsPackage extends IDataBasic {
  rights_template_id: number;
  title: string;
  sub_title: string;
  price: number;
  org_price_description: string;
  credit_coins: number;
  free_credit_coins: number;
  total_credit_coins: number;
  idx: number;
}
export function getRightsCreditCoinsPackage(params: IGetRightsCreditCoinsPackageParams) {
  return fetch<IResponse<ICreditCoinsPackage[]>>('/rights/credit_coins_package/', {
    method: 'GET',
    params,
  });
}

// 已购言币包
export function getOrderClientBoughtCoinsPackageList(params: {
  check_all: number; // 0=当前， 1=全部
}) {
  return fetch<IResponse<ICreditCoinsPackage[]>>('/order/client/bought_coins_package_list/', {
    method: 'GET',
    params,
  });
}

// 剩余可加购言币
export function getOrderClientRemainedBuyCreditCoinsCount() {
  return fetch<IResponse<{ remained_credit_coins: number }>>(
    '/order/client/remained_buy_credit_coins_count/',
    {
      method: 'GET',
    }
  );
}

interface ISubscriptionCreateBody {
  rights_id: number;
  behavior: ORDER_BEHAVIOR;
}

/**
 * Creates a new subscription for a client by sending a POST request to the '/order/client/subscription/create/' endpoint.
 *
 * @param {ISubscriptionCreateBody} body - The body of the request containing the necessary information for creating a subscription.
 * @return {Promise<IResponse<ISubscription>>} A promise that resolves to the response containing the created subscription.
 */
export function postOrderClientSubscriptionCreate(
  body: ISubscriptionCreateBody,
  config: RequestConfig
) {
  const req = useRequestURL();
  const isMock = req.searchParams.has('mock');

  if (isMock) {
    body = Object.assign(body, {
      mock: {
        order_error: 'ACQ.CYCLE_PAY_TIME_NOT_SUPPORT',
        sub_status: 'VERIFIED',
      },
    });
  }
  return fetch<IResponse<ISubscription>>('/order/client/subscription/create/', {
    method: 'POST',
    body,
    ...config,
  });
}

/**
 * Retrieves the subscription item for a given order client subscription ID.
 *
 * @param {number} id - The ID of the order client subscription.
 * @return {Promise<IResponse<ISubscription>>} A promise that resolves to the subscription item.
 */
export function getOrderClientSubscriptionItem(id: number, config: any) {
  return fetch<IResponse<ISubscription>>('/order/client/subscription', {
    method: 'GET',
    params: {
      id,
    },
    ...config,
  });
}

export interface ISubscriptionStatus {
  subscription: {
    status: number;
    behavior: number;
    show_qr_code: boolean;
    pay_error: string;
  };
  order: {
    behavior: ORDER_BEHAVIOR;
    id: number;
    pay_fee: number;
    qr_code_url: string;
    status: PAY_STATUS;
    rights_name: string;
    start_timestamp: number;
    expired_timestamp: number;
  };
}

export function getSubscriptionStatus(id: number, config: any) {
  return fetch<IResponse<ISubscriptionStatus>>('/order/client/subscription/status', {
    method: 'GET',
    params: {
      id,
    },
    ...config,
  });
}

/**
 * Sends a POST request to create an order for a client subscription.
 *
 * @param {IPostCreateOrderConfig} body - The configuration for creating the order.
 * @param {Record<string, string>} [headers] - Optional headers for the request.
 * @return {Promise<IResponse<IOrder>>} A promise that resolves to the response containing the created order.
 */
export function postCreateOrder(body: IPostCreateOrderConfig, config?: RequestConfig) {
  const discount_code = new URL(location.href).searchParams.get(DISCOUNT_CODE);
  if (discount_code) {
    body.discount_code = discount_code;
  }

  body = omit(body, ['first_name', 'last_name']);

  return fetch<IResponse<IOrder>>('/order/client/subscription/', {
    method: 'POST',
    body,
    headers: {
      ...config?.headers,
    },
    signal: config?.signal,
  });
}

/**
 * Deletes the order subscription by sending a DELETE request to '/order/client/subscription/'.
 *
 * @return {Promise<IResponse>} A promise that resolves to the response of the delete request.
 */
export function deleteOrderSubscription() {
  return fetch<IResponse>('/order/client/subscription/', {
    method: 'DELETE',
  });
}

/**
 * Sends a request to cancel a subscription confirmation with the given cancel secret.
 *
 * @param {string} cancel_secret - The secret used to confirm the cancellation.
 * @return {Promise<IResponse>} A promise that resolves to the response from the server.
 */
export function postOrderSubscriptionCancelConfirm(cancel_secret: string) {
  return fetch<IResponse>('/order/client/subscription/cancel_confirm/', {
    method: 'POST',
    credential: false,
    body: {
      cancel_secret,
    },
  });
}

/**
 * Retrieves the subscription details for the current user.
 *
 * @return {Promise<IResponse<ISubscription>>} A promise that resolves to the subscription details.
 */
export function getOrderClientSubscriptionMy() {
  return fetch<IResponse<ISubscription>>('/order/client/subscription/my/', {
    method: 'GET',
  });
}

/**
 * Retrieves the order client detail based on the provided ID.
 *
 * @param {number} no - The ID of the order client.
 * @return {Promise<IResponse<IOrder>>} A promise that resolves to the order client detail.
 */
export function getOrderClientDetail(no: string) {
  return fetch<IResponse<IOrder>>('/order/client/detail/', {
    method: 'GET',
    params: { no },
  });
}

export interface IStripePaymentBasic extends IAirWallexPaymentBasic {
  success_url?: string;
  cancel_url?: string;
}

export interface IPostBuySubAccountBody extends IStripePaymentBasic {
  pay_type: PAY_TYPE;
  num: number;
}
export function postBuySubAccount(body: IPostBuySubAccountBody, config?: RequestConfig) {
  return fetch<IResponse<IOrder>>('/order/client/buy_sub_account/', {
    method: 'POST',
    body,
    ...config,
  });
}

export interface IPostOrderClientCancel {
  id: number;
}

export function postOrderClientCancel(body: IPostOrderClientCancel, config?: RequestConfig) {
  return fetch<IResponse<IOrder>>('/order/client/cancel/', {
    method: 'POST',
    body,
    ...config,
  });
}

// 获取 stripe 的取消订阅的管理界面
// /order/client/subscription/portal_page/
export function postSubscriptionPortalPage(return_url: string) {
  return fetch<IResponse<{ customer_portal_url: string }>>(
    '/order/client/subscription/portal_page/',
    {
      method: 'POST',
      body: {
        return_url,
      },
    }
  );
}
